import { useState, type ReactElement } from "react";
import { FormattedMessage, defineMessages, useIntl, type MessageDescriptor } from "react-intl";
import classnames from "classnames";

import { StyledTextInput } from "common/core/form/text";
import { Select } from "common/core/form/select";
import type { UseFormRegisterReturn, UseFormReturn } from "common/core/form";
import Button from "common/core/button";
import Tooltip from "common/core/tooltip";
import Icon from "common/core/icon";
import { useMobileScreenClass } from "common/core/responsive";

import Styles from "./index.module.scss";

export enum Pronouns {
  SHE_HER = "She/her",
  HE_HIM = "He/him",
  THEY_THEM = "They/them",
  OTHER = "Other",
}

export function getPronounsFormDataForUser({ pronouns: userPronouns }: { pronouns?: Pronouns }) {
  const pronouns = userPronouns
    ? Object.values(Pronouns).includes(userPronouns)
      ? userPronouns
      : Pronouns.OTHER
    : undefined;
  return {
    pronouns,
    customPronouns: userPronouns,
  };
}

export type FormValues = {
  pronouns: Pronouns;
  customPronouns?: string;
};

type HookFormProps = {
  selectedPronouns: Pronouns | undefined;
  pronounsProps: UseFormRegisterReturn<"pronouns">;
  customPronounsProps: UseFormRegisterReturn<"customPronouns">;
};

export const PRONOUNS_MAP: Record<
  FormValues["pronouns"],
  { label: ReactElement; ariaLabel?: MessageDescriptor }
> = {
  "She/her": {
    label: <FormattedMessage id="51cc78ba-38eb-4467-bbc1-fd8c22a99c16" defaultMessage="She/her" />,
  },
  "He/him": {
    label: <FormattedMessage id="4ff1e7df-baa5-40ca-b648-6f6e2c6a57a8" defaultMessage="He/him" />,
  },
  "They/them": {
    label: (
      <FormattedMessage id="2242dfb5-f757-4cad-840f-0c9ea5875f89" defaultMessage="They/them" />
    ),
  },
  Other: {
    label: (
      <FormattedMessage
        id="1294af61-4d72-452a-aec4-d551762b4131"
        defaultMessage="Not listed, please specify"
      />
    ),
    ariaLabel: {
      id: "68f57f5e-5341-4af2-b33e-33e272b046c3",
      defaultMessage: "Not listed, please specify your custom pronouns below",
    },
  },
};

export const PREDEFINED_PRONOUNS = Object.keys(PRONOUNS_MAP).map((pronoun) => ({
  value: pronoun,
  label: PRONOUNS_MAP[pronoun as FormValues["pronouns"]].label,
  ariaLabel: PRONOUNS_MAP[pronoun as FormValues["pronouns"]].ariaLabel,
}));

const LABEL_MESSAGES = defineMessages({
  pronouns: {
    id: "9aa7ae92-9340-4c92-b25a-39aeb291e640",
    defaultMessage: "Pronouns (optional)",
  },
  customPronouns: {
    id: "b3b0976a-0c5d-4455-93b2-1f5243f9a120",
    defaultMessage: "Custom pronouns",
  },
  triggerTooltipLabelPronouns: {
    id: "de4b8bfb-27f0-4078-b5ba-e2073dc1c9b9",
    defaultMessage: "More details about adding pronouns",
  },
});

const PLACEHOLDER_MESSAGES = defineMessages({
  customPronouns: {
    id: "408540de-865a-466b-ad99-fd1dbb0f56bf",
    defaultMessage: "Specify your pronouns",
  },
});

const TOOLTIP_OFFSET = 127;

export function PronounsButtonSubForm({
  className,
  pronounsProps,
  customPronounsProps,
  selectedPronouns,
  showTooltip = true,
  resetField,
}: HookFormProps & {
  className?: string;
  showTooltip?: boolean;
  resetField: UseFormReturn<FormValues>["resetField"];
}) {
  const intl = useIntl();
  const [formOpen, setFormOpen] = useState(Boolean(selectedPronouns));
  const isMobile = useMobileScreenClass();

  return (
    <>
      <Button
        className={classnames(Styles.pronounsButton, className)}
        aria-expanded={formOpen}
        automationId="pronouns"
        variant="tertiary"
        buttonColor="action"
        buttonSize="condensed"
        onClick={() =>
          setFormOpen((isOpen) => {
            const newIsOpen = !isOpen;
            if (!newIsOpen) {
              resetField("pronouns");
              resetField("customPronouns");
            }
            return !isOpen;
          })
        }
      >
        <FormattedMessage
          id="9037f008-d1ad-4440-a7d8-edf7cbb690df"
          defaultMessage="{isOpen, select, true{-} other{+}} Add pronouns (optional)"
          values={{ isOpen: formOpen }}
        />
      </Button>
      {showTooltip && (
        <Tooltip
          className={Styles.tooltip}
          target={<Icon name="question" />}
          triggerButtonLabel={intl.formatMessage(LABEL_MESSAGES.triggerTooltipLabelPronouns)}
          offsetTop={isMobile ? TOOLTIP_OFFSET : undefined}
          selfManagePlacement
        >
          <FormattedMessage
            id="f4a37ecf-ae9a-45b7-8d12-4b9038716b62"
            defaultMessage="We are committed to your safety and respect. This information will only be used during video calls."
          />
        </Tooltip>
      )}

      {formOpen && (
        <PronounsSubHookForm
          selectedPronouns={selectedPronouns}
          pronounsProps={pronounsProps}
          customPronounsProps={customPronounsProps}
        />
      )}
    </>
  );
}

export function PronounsSubHookForm({
  pronounsProps,
  customPronounsProps,
  selectedPronouns,
}: HookFormProps) {
  const intl = useIntl();
  const { onChange, ...otherPronounsProps } = pronounsProps;

  return (
    <>
      <Select
        aria-invalid={false}
        data-automation-id="pronouns-field"
        items={PREDEFINED_PRONOUNS}
        aria-label={intl.formatMessage(LABEL_MESSAGES.pronouns)}
        onChange={(event) => {
          onChange(event);
        }}
        {...otherPronounsProps}
      />
      {selectedPronouns === Pronouns.OTHER && (
        <div className={Styles.customPronouns}>
          <StyledTextInput
            id="customPronouns"
            label={intl.formatMessage(LABEL_MESSAGES.customPronouns)}
            aria-invalid={false}
            placeholder={intl.formatMessage(PLACEHOLDER_MESSAGES.customPronouns)}
            {...customPronounsProps}
          />
        </div>
      )}
    </>
  );
}
